import styled from '../../styled';

/**
 * Optimize mockups reuse the light product chrome from the Amplify
 * mockups so every coded product frame on the site looks identical.
 */
export {
  ProductMockupBody,
  ProductMockupFrame
} from '../AmplifyProductMockups/shared';

export { AppMockupChrome } from '../AppMockupChrome';

/**
 * Gray placeholder bar standing in for non-essential text (page titles,
 * body copy) inside mockups, mirroring the skeleton style of the
 * original product screenshots.
 */
export const SkeletonBar = styled('span')<{
  w?: number | string;
  h?: number;
}>`
  display: inline-block;
  width: ${(p) => (typeof p.w === 'number' ? `${p.w}px` : p.w || '80px')};
  height: ${(p) => p.h || 7}px;
  border-radius: 100px;
  background: #e7ebef;
  vertical-align: middle;
`;

export type ChipTone = 'best' | 'good' | 'ok' | 'warn' | 'none';

const CHIP_STYLES: Record<ChipTone, { bg: string; color: string }> = {
  best: { bg: '#1d3d0c', color: '#fff' },
  good: { bg: '#389e0d', color: '#fff' },
  ok: { bg: '#d9f7be', color: '#237804' },
  warn: { bg: '#fff1b8', color: '#d48806' },
  none: { bg: '#f1f5f9', color: '#94a3b8' }
};

/**
 * Color-coded metric chip used for Page CTR values, matching the
 * product's green-to-amber scale (83.2% dark green ... 0% gray).
 */
export const MetricChip = styled('span')<{ tone: ChipTone }>`
  display: inline-block;
  font-size: 9.5px;
  font-weight: 700;
  border-radius: 100px;
  padding: 3px 9px;
  background: ${(p) => CHIP_STYLES[p.tone].bg};
  color: ${(p) => CHIP_STYLES[p.tone].color};
  white-space: nowrap;
`;

export type TrendTone = 'up' | 'partial' | 'flat' | 'down';

const TREND_COLOR: Record<TrendTone, string> = {
  up: '#52c41a',
  partial: '#fa8c16',
  flat: '#b0b7c3',
  down: '#f5222d'
};

/**
 * Small trend arrow next to a metric: green up, orange diagonal
 * (partial growth), gray flat, red down. Pure CSS/SVG, no deps.
 */
export const TrendIcon = ({ tone }: { tone: TrendTone }) => {
  const rotation =
    tone === 'up' ? -90 : tone === 'partial' ? -45 : tone === 'down' ? 90 : 0;
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke={TREND_COLOR[tone]}
      strokeWidth="3.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ transform: `rotate(${rotation}deg)`, flexShrink: 0 }}
      aria-hidden
    >
      <line x1="3" y1="12" x2="21" y2="12" />
      <polyline points="13 4 21 12 13 20" />
    </svg>
  );
};

/** Skeleton metric cell: gray bar plus a trend arrow, right-aligned. */
export const MetricCell = styled('span')`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
  white-space: nowrap;
`;

/** Rank-movement pill (+13 green, -1 red, "–" gray). */
export const RankChip = styled('span')<{ tone: 'up' | 'down' | 'flat' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  height: 20px;
  padding: 0 6px;
  box-sizing: border-box;
  border-radius: 100px;
  font-size: 9.5px;
  font-weight: 700;
  background: ${(p) =>
    p.tone === 'up' ? '#f0fbe4' : p.tone === 'down' ? '#fff0ef' : '#f1f5f9'};
  color: ${(p) =>
    p.tone === 'up' ? '#389e0d' : p.tone === 'down' ? '#f5222d' : '#94a3b8'};
`;

/** White card sitting on the mockup's warm-gray canvas. */
export const MockCard = styled('div')`
  background: #fff;
  border: 1px solid #ececea;
  border-radius: 10px;
  min-width: 0;
`;
